import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GripDesignSet } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { createAppAsyncThunk } from '../actions/thunkActionTypes';

type ApiState<T = null> =
  | { readonly status: 'pending' }
  | { readonly status: 'running' }
  | { readonly status: 'succeeded'; readonly data: T }
  | { readonly status: 'failed'; readonly message: string };

type PlateGripDesignValuesState = {
  remote: ApiState<GripDesignSet[]>;
  save: ApiState;
  current: Partial<GripDesignSet>[];
};

const initialState: PlateGripDesignValuesState = {
  remote: { status: 'pending' },
  current: [],
  save: { status: 'succeeded', data: null },
};

const SLICE_NAME = 'plateGripDesignValues';

// https://redux.js.org/tutorials/essentials/part-5-async-logic#reducers-and-loading-actions
export const loadGripValues = createAppAsyncThunk(
  `${SLICE_NAME}/load`,

  async (_, { dispatch }) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading...'));
    try {
      return await designService.platePairs.getOrganizationGripValues();
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Failed to load your organization's grip design values.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  },
  {
    condition(_, { getState }) {
      return getState().plateGrip.remote.status !== 'running';
    },
  },
);

export const saveGripValues = createAppAsyncThunk(
  `${SLICE_NAME}/save`,
  async (_, { dispatch, getState }) => {
    const spinnerId = dispatch(addSpinnerMessage('Saving...'));
    const values = getState().plateGrip.current;
    try {
      return await designService.platePairs.saveOrganizationGripValues(values as any);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Failed to save your organization's grip design values.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  },
  {
    condition(_, { getState }) {
      return getState().plateGrip.save.status !== 'running';
    },
  },
);

const slice = createSlice({
  name: SLICE_NAME,
  initialState: initialState,
  extraReducers: (builder) =>
    builder
      .addCase(loadGripValues.pending, (state) => {
        state.remote = { status: 'running' };
      })
      .addCase(loadGripValues.fulfilled, (state, action) => {
        state.remote = { status: 'succeeded', data: action.payload! };
        state.current = action.payload!;
      })
      .addCase(loadGripValues.rejected, (state, action) => {
        state.remote = {
          status: 'failed',
          message: action.error.message ?? 'Unknown Error',
        };
      })
      .addCase(saveGripValues.pending, (state) => {
        state.save = { status: 'running' };
      })
      .addCase(saveGripValues.fulfilled, (state) => {
        state.save = { status: 'succeeded', data: null };
      })
      .addCase(saveGripValues.rejected, (state, action) => {
        state.save = {
          status: 'failed',
          message: action.error.message ?? 'Unknown Error',
        };
      }),
  reducers: {
    deleteRow(state, action: PayloadAction<number>) {
      state.save = { status: 'pending' };
      state.current.splice(action.payload, 1);
    },
    addRow(state) {
      state.save = { status: 'pending' };
      const n = state.current.length;
      state.current.push({ source: state.current[n - 1]?.source, netAreaEdgeOffset: 0, netAreaEndOffset: 0 });
    },
    changeCell<K extends keyof GripDesignSet>(
      state: PlateGripDesignValuesState,
      action: PayloadAction<{ row: number; column: K; value: GripDesignSet[K] }>,
    ) {
      state.save = { status: 'pending' };
      state.current[action.payload.row][action.payload.column] = action.payload.value;
    },
    restoreToLoaded(state) {
      state.save = { status: 'succeeded', data: null };
      if (state.remote.status === 'succeeded') {
        state.current = state.remote.data;
      }
    },
  },
});

export const { deleteRow, addRow, changeCell, restoreToLoaded } = slice.actions;
export const plateGrip = slice.reducer;
